import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Logout,
  Dashboard,
  Settings,
  Menu as MenuIcon,
  Add,
  Description,
  Palette,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { authService } from '../../services/authService';
import { consultationService } from '../../services/consultationService';
import { SubscriptionBanner } from '../subscription/SubscriptionBanner';
import { UserRole } from '../../types';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const drawerWidth = 270;

interface NavItem {
  title: string;
  icon: JSX.Element;
  path?: string;
}

interface NavSection {
  section: string;
  items: NavItem[];
}

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [startingConsultation, setStartingConsultation] = useState(false);
  const currentVetName = useAuthStore((state) => state.currentVetName);
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const isAdmin = user?.role === UserRole.ADMIN;

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleStartNewConsultation = async () => {
    setStartingConsultation(true);
    try {
      // Check if template prefill is enabled (default to true)
      const prefillEnabled = localStorage.getItem('template_prefill_enabled') !== 'false';

      const newConsultation = await consultationService.startConsultation({
        prefill_with_template: prefillEnabled
      });
      // Request scraping before navigating — the new ConsultationPage will be
      // mounted and its VETAI_INJECT_CONTEXT listener active before the extension responds.
      window.parent.postMessage({ type: 'REFRESH_CONTEXT' }, '*');
      navigate(`/consultation/${newConsultation.id}`);
      if (isMobile) {
        setMobileOpen(false);
      }
    } catch (err) {
      console.error('Failed to start consultation:', err);
    } finally {
      setStartingConsultation(false);
    }
  };

  const navSections: NavSection[] = [
    {
      section: t('navSections.home'),
      items: [
        {
          title: t('navigation.history'),
          icon: <Dashboard />,
          path: '/history',
        },
        {
          title: t('templates.title'),
          icon: <Description />,
          path: '/templates',
        },
        {
          title: t('reportFormatting.navTitle'),
          icon: <Palette />,
          path: '/report-formatting',
        },
        {
          title: t('navigation.settings'),
          icon: <Settings />,
          path: '/settings',
        },
        {
          title: t('navigation.subscription'),
          icon: <CreditCardIcon />,
          path: '/subscription',
        },
        {
          title: t('navigation.support'),
          icon: <SupportAgentIcon />,
          path: '/support',
        },
        ...(isAdmin
          ? [{ title: t('navigation.admin'), icon: <AdminPanelSettingsIcon />, path: '/admin' }]
          : []),
      ],
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo/Branding Section */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="SynaVet"
            sx={{
              height: 40,
              width: 'auto',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              letterSpacing: '-0.5px',
              background: 'linear-gradient(to right, #1565c0, #42a5f5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            SynaVet
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Current Vet Indicator */}
      {currentVetName && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'primary.light',
              border: '1px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              {t('auth.connectedVet')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 12 }}>
                {currentVetName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {currentVetName}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* New Consultation Button */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={startingConsultation ? <CircularProgress size={16} color="inherit" /> : <Add />}
          onClick={handleStartNewConsultation}
          disabled={startingConsultation}
          sx={{ py: 1.5 }}
        >
          {t('consultation.startNewConsultation')}
        </Button>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* Navigation Sections */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {navSections.map((section) => (
          <Box key={section.section} sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ px: 2, mb: 1, display: 'block' }}
            >
              {section.section}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => (
                <ListItemButton
                  key={item.title}
                  selected={isActive(item.path!)}
                  onClick={() => handleNavigation(item.path!)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={isActive(item.path!) ? 600 : 500}>
                        {item.title}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Logout Section */}
      <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            bgcolor: 'error.light',
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.main',
              color: 'white',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Logout sx={{ color: 'inherit' }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" fontWeight={600}>
                {t('auth.logout')}
              </Typography>
            }
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar Navigation */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 3, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
        }}
      >
        {/* Subscription status banner */}
        <SubscriptionBanner />

        {/* Mobile Header */}
        {isMobile && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
                size="small"
              >
                <MenuIcon />
              </IconButton>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  letterSpacing: '-0.3px',
                  color: '#5D87FF',
                }}
              >
                SynaVet
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={startingConsultation ? <CircularProgress size={16} color="inherit" /> : <Add />}
              onClick={handleStartNewConsultation}
              disabled={startingConsultation}
              sx={{ minWidth: 0, px: 2 }}
            >
              {t('consultation.new')}
            </Button>
          </Box>
        )}

        <Outlet />
      </Box>
    </Box>
  );
};
